import { useMutation } from '@apollo/client';
import { AnalyticsEvent, SubscriptionApp } from '@focuslee/shared';
import { invoice } from '@tma.js/sdk';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmAction, showErrorPopup } from '../helpers/dialogs';
import { cancelMySubscriptionMutation, createPaymentInvoiceMutation, startTrialMutation } from './operations';
import { openPaymentInvoice } from './payment';
import { clearMissedRenewal } from './premiumCache';
import { getPremiumState, PremiumUser } from './premiumState';
import { usePaymentConfirmation } from './usePaymentConfirmation';

export function usePremiumActions(
    user: PremiumUser | undefined,
    app: SubscriptionApp,
    source: string,
    onTrialStarted: () => void,
    onEntitlementChanged: (user: PremiumUser) => void,
    recordEvent: (event: string, properties: Record<string, string | number>) => void,
) {
    const { t } = useTranslation('subscription');
    const { confirmPayment, confirming } = usePaymentConfirmation(onEntitlementChanged);
    const [openingInvoice, setOpeningInvoice] = useState(false);
    const [createInvoice] = useMutation<
        { createPaymentInvoice: { paymentId: string; invoiceUrl: string } },
        { productKey: string; app: SubscriptionApp }
    >(createPaymentInvoiceMutation);
    const [cancelSubscription, { loading: canceling }] = useMutation(cancelMySubscriptionMutation);
    const [startTrial, { loading: startingTrial }] = useMutation<
        { startTrial: { ok: boolean; errorMessage?: string | null; user?: PremiumUser | null } },
        { app: SubscriptionApp }
    >(startTrialMutation, {
        update: (cache, result) => {
            if (result.data?.startTrial.ok) clearMissedRenewal(cache);
        },
    });

    const handleSubscribe = async () => {
        if (!user || confirming || openingInvoice) return;
        const { productKey, product } = getPremiumState(user);
        setOpeningInvoice(true);
        try {
            recordEvent(AnalyticsEvent.checkoutStarted, { product_id: productKey, stars_amount: product.amount, source });
            await openPaymentInvoice(productKey, {
                isInvoiceAvailable: () => invoice.openUrl.isAvailable(),
                isInvoiceOpened: () => invoice.isOpened(),
                createInvoice: async key => {
                    const { data } = await createInvoice({ variables: { productKey: key, app } });
                    return data?.createPaymentInvoice ?? null;
                },
                openInvoiceUrl: url => invoice.openUrl(url),
                onPaid: confirmPayment,
            });
        } catch (error) {
            console.error(error);
            showErrorPopup('Payment', t('paymentError'));
        } finally {
            setOpeningInvoice(false);
        }
    };

    const handleCancel = async () => {
        if (!(await confirmAction(t('cancelSubscription'), t('cancelConfirm')))) return;
        try {
            await cancelSubscription();
        } catch (error) {
            console.error(error);
            showErrorPopup('Subscription', t('cancelError'));
        }
    };

    const handleStartTrial = async () => {
        try {
            const { data } = await startTrial({ variables: { app } });
            if (!data?.startTrial.ok) {
                showErrorPopup('Trial', data?.startTrial.errorMessage ?? t('trialError'));
                return;
            }
            if (data.startTrial.user) onEntitlementChanged(data.startTrial.user);
            onTrialStarted();
        } catch (error) {
            console.error(error);
            showErrorPopup('Trial', t('trialError'));
        }
    };

    return { handleSubscribe, handleCancel, handleStartTrial, canceling, startingTrial, subscribing: openingInvoice || confirming };
}
